@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800;900&family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&display=swap');

:root {
  /* Dark Theme Tokens (Default) */
  --bg-gradient: radial-gradient(circle at 50% -20%, rgba(16, 185, 129, 0.15), transparent 45rem),
                 radial-gradient(circle at 10% 40%, rgba(59, 130, 246, 0.08), transparent 30rem),
                 linear-gradient(160deg, #090e0c, #040605 100%);
  --app-bg: rgba(9, 13, 12, 0.75);
  --shell-border: #141c19;
  --glass-bg: rgba(20, 28, 25, 0.45);
  --glass-bg-hover: rgba(28, 38, 34, 0.65);
  --glass-border: rgba(255, 255, 255, 0.06);
  --glass-border-focus: rgba(16, 185, 129, 0.4);
  --text-primary: #f3f4f6;
  --text-secondary: #9ca3af;
  --text-muted: #6b7280;
  --border-color: rgba(255, 255, 255, 0.07);
  --input-bg: rgba(13, 19, 17, 0.8);
  --nav-bg: rgba(9, 13, 12, 0.85);
  
  /* Primary Palette */
  --brand: #10b981; /* Emerald */
  --brand-light: #34d399;
  --brand-glow: rgba(16, 185, 129, 0.25);
  --accent: #a3e635; /* Lime */
  --accent-glow: rgba(163, 230, 53, 0.2);
  
  /* Priority/Status Colors */
  --color-normal: #3b82f6;
  --bg-normal: rgba(59, 130, 246, 0.12);
  --color-high: #f59e0b;
  --bg-high: rgba(245, 158, 11, 0.12);
  --color-urgent: #ef4444;
  --bg-urgent: rgba(239, 68, 68, 0.12);
  
  --status-new: #9ca3af;
  --status-new-bg: rgba(156, 163, 175, 0.1);
  --status-assigned: #f59e0b;
  --status-assigned-bg: rgba(245, 158, 11, 0.1);
  --status-progress: #3b82f6;
  --status-progress-bg: rgba(59, 130, 246, 0.1);
  --status-review: #a855f7;
  --status-review-bg: rgba(168, 85, 247, 0.1);
  
  --shadow-lg: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
  --shadow-glow: 0 0 20px rgba(16, 185, 129, 0.15);
  --transition-smooth: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  --transition-spring: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

/* Light Theme Variables Overrides */
.light-theme {
  --bg-gradient: radial-gradient(circle at 50% -20%, rgba(16, 185, 129, 0.12), transparent 45rem),
                 radial-gradient(circle at 10% 40%, rgba(59, 130, 246, 0.05), transparent 30rem),
                 linear-gradient(160deg, #f0f4f2, #e5ebe8 100%);
  --app-bg: rgba(255, 255, 255, 0.85);
  --shell-border: #e2e8f0;
  --glass-bg: rgba(255, 255, 255, 0.7);
  --glass-bg-hover: rgba(255, 255, 255, 0.95);
  --glass-border: rgba(16, 185, 129, 0.12);
  --glass-border-focus: rgba(16, 185, 129, 0.6);
  --text-primary: #0f172a;
  --text-secondary: #475569;
  --text-muted: #64748b;
  --border-color: rgba(16, 185, 129, 0.08);
  --input-bg: rgba(248, 250, 252, 0.9);
  --nav-bg: rgba(255, 255, 255, 0.9);
  --shadow-lg: 0 25px 50px -12px rgba(15, 23, 42, 0.08);
  --shadow-glow: 0 0 20px rgba(16, 185, 129, 0.08);
}

* {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

html, body, #root {
  min-height: 100%;
  width: 100%;
  font-family: 'Outfit', 'Plus Jakarta Sans', sans-serif;
  overflow-x: hidden;
}

body {
  color: var(--text-primary);
  background: var(--bg-gradient);
  transition: var(--transition-smooth);
}

button, select, input, textarea {
  font-family: inherit;
  font-size: 1rem;
  color: inherit;
  background: transparent;
  border: none;
  outline: none;
}

button {
  cursor: pointer;
  transition: var(--transition-spring);
}

/* Scrollbar Customization */
::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}
::-webkit-scrollbar-track {
  background: transparent;
}
::-webkit-scrollbar-thumb {
  background: rgba(16, 185, 129, 0.2);
  border-radius: 99px;
}
::-webkit-scrollbar-thumb:hover {
  background: rgba(16, 185, 129, 0.4);
}

/* Desktop App Container */
.desktop-wrapper {
  display: flex;
  min-height: 100vh;
  width: 100vw;
  gap: 2rem;
  padding: 2.5rem;
  justify-content: center;
  align-items: center;
}

@media (max-width: 1024px) {
  .desktop-wrapper {
    flex-direction: column;
    padding: 1.5rem;
    gap: 1.5rem;
  }
}

/* Control Panel Widget */
.control-panel {
  flex: 0 1 360px;
  background: var(--glass-bg);
  backdrop-filter: blur(24px);
  border: 1px solid var(--glass-border);
  border-radius: 28px;
  padding: 2rem;
  box-shadow: var(--shadow-lg);
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  align-self: stretch;
}

.control-panel h2 {
  font-size: 1.4rem;
  font-weight: 800;
  letter-spacing: -0.03em;
  background: linear-gradient(135deg, var(--text-primary), var(--brand));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.control-panel p {
  font-size: 0.9rem;
  color: var(--text-secondary);
  line-height: 1.5;
}

.control-btn-group {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.panel-btn {
  width: 100%;
  padding: 0.85rem 1.25rem;
  border-radius: 16px;
  font-weight: 600;
  font-size: 0.95rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid var(--border-color);
  color: var(--text-primary);
  text-align: left;
}

.panel-btn:hover {
  background: var(--glass-bg-hover);
  transform: translateY(-2px);
  border-color: var(--brand-glow);
}

.panel-btn.active {
  background: var(--brand-glow);
  border-color: var(--brand);
  color: var(--brand-light);
}

.tech-specs {
  border-top: 1px solid var(--border-color);
  padding-top: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.spec-row {
  display: flex;
  justify-content: space-between;
  font-size: 0.8rem;
  font-weight: 500;
}

.spec-label {
  color: var(--text-muted);
}

.spec-val {
  color: var(--text-secondary);
  font-family: monospace;
}

/* Mobile Phone Shell container */
.mobile-app {
  flex: 0 0 auto;
  display: grid;
  place-items: center;
  position: relative;
}

.app-shell {
  width: 395px;
  height: 840px;
  border-radius: 48px;
  overflow: hidden;
  background: var(--app-bg);
  backdrop-filter: blur(28px);
  box-shadow: var(--shadow-lg), 0 0 0 12px var(--shell-border), 0 20px 60px rgba(0,0,0,0.4);
  border: 4px solid rgba(255, 255, 255, 0.05);
  position: relative;
  display: flex;
  flex-direction: column;
  transition: var(--transition-smooth);
}

/* Notch display simulation */
.app-notch {
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 140px;
  height: 28px;
  background: var(--shell-border);
  border-bottom-left-radius: 18px;
  border-bottom-right-radius: 18px;
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
}

.app-notch::before {
  content: '';
  width: 44px;
  height: 4px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 99px;
}

/* Screen Body */
.screen-body {
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 2.2rem 1.25rem 6.5rem;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.screen-body::-webkit-scrollbar {
  display: none;
}

/* Top Bar Styling */
.topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 0.25rem 0.75rem;
  position: relative;
  z-index: 10;
}

.topbar button {
  width: 42px;
  height: 42px;
  border-radius: 14px;
  background: var(--glass-bg);
  border: 1px solid var(--glass-border);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-primary);
  position: relative;
}

.topbar button:hover {
  background: var(--glass-bg-hover);
  border-color: var(--brand);
}

.topbar button i {
  position: absolute;
  top: 10px;
  right: 10px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--color-urgent);
  box-shadow: 0 0 8px var(--color-urgent);
}

.topbar-center {
  text-align: center;
}

.topbar-center span {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--brand);
  display: block;
}

.topbar-center strong {
  font-size: 1.35rem;
  font-weight: 800;
  letter-spacing: -0.03em;
  color: var(--text-primary);
  display: block;
  margin-top: 2px;
}

/* Hero / Today Dashboard Card */
.today-card {
  border-radius: 24px;
  padding: 1.5rem;
  color: white;
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.85), rgba(6, 95, 70, 0.95)),
              radial-gradient(circle at 90% 10%, rgba(163, 230, 53, 0.4), transparent 12rem);
  border: 1px solid rgba(255, 255, 255, 0.12);
  box-shadow: var(--shadow-glow), var(--shadow-lg);
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  position: relative;
  overflow: hidden;
}

.today-card::before {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(to right, transparent, rgba(255, 255, 255, 0.05), transparent);
  transform: translateX(-100%);
  animation: shine 4s infinite linear;
}

@keyframes shine {
  100% { transform: translateX(100%); }
}

.today-card span {
  font-size: 0.8rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.8);
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.today-card strong {
  font-size: 2rem;
  font-weight: 800;
  letter-spacing: -0.04em;
  line-height: 1.1;
  display: block;
  margin-top: 4px;
}

.today-card p {
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.4;
}

.today-card button {
  background: var(--accent);
  color: #0b1c17;
  padding: 0.75rem 1.25rem;
  border-radius: 14px;
  font-weight: 700;
  font-size: 0.9rem;
  align-self: flex-start;
  box-shadow: 0 4px 14px rgba(163, 230, 53, 0.3);
}

.today-card button:hover {
  transform: scale(1.04);
  box-shadow: 0 6px 18px rgba(163, 230, 53, 0.5);
}

/* Quick Metrics Grid */
.quick-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.75rem;
}

.quick-card {
  background: var(--glass-bg);
  border: 1px solid var(--glass-border);
  border-radius: 20px;
  padding: 1.1rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  transition: var(--transition-spring);
}

.quick-card:hover {
  background: var(--glass-bg-hover);
  border-color: var(--brand-glow);
  transform: translateY(-2px);
}

.quick-card svg {
  color: var(--brand);
}

.quick-card strong {
  font-size: 1.75rem;
  font-weight: 800;
  letter-spacing: -0.03em;
  line-height: 1;
}

.quick-card span {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--text-secondary);
}

/* List Headers */
.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.25rem;
}

.section-header strong {
  font-size: 1.1rem;
  font-weight: 800;
  letter-spacing: -0.02em;
}

.section-header button {
  font-size: 0.8rem;
  font-weight: 700;
  color: var(--brand-light);
  background: var(--brand-glow);
  padding: 0.4rem 0.8rem;
  border-radius: 99px;
}

/* Card Lists */
.card-list {
  background: var(--glass-bg);
  border: 1px solid var(--glass-border);
  border-radius: 24px;
  padding: 0.5rem 1rem;
  display: flex;
  flex-direction: column;
}

/* Record Rows */
.record-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 0;
  border-bottom: 1px solid var(--border-color);
  gap: 0.75rem;
}

.record-row:last-child {
  border-bottom: none;
}

.record-row:hover {
  background: rgba(255, 255, 255, 0.01);
}

.priority-bar {
  width: 4px;
  height: 36px;
  border-radius: 99px;
  flex-shrink: 0;
}
.priority-bar.normal { background: var(--color-normal); }
.priority-bar.high { background: var(--color-high); }
.priority-bar.urgent { background: var(--color-urgent); }

.record-info {
  flex: 1 1 auto;
}

.record-info strong {
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--text-primary);
  display: block;
}

.record-info span {
  font-size: 0.75rem;
  color: var(--text-secondary);
  display: block;
  margin-top: 2px;
}

.record-status-col {
  text-align: right;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
}

.record-status-col small {
  font-size: 0.7rem;
  color: var(--text-muted);
  font-weight: 500;
}

/* Custom Status Chips */
.status-pill {
  font-size: 0.7rem;
  font-weight: 700;
  padding: 0.25rem 0.6rem;
  border-radius: 99px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.status-pill.new { background: var(--status-new-bg); color: var(--status-new); }
.status-pill.assigned { background: var(--status-assigned-bg); color: var(--status-assigned); }
.status-pill.in-progress { background: var(--status-progress-bg); color: var(--status-progress); }
.status-pill.review { background: var(--status-review-bg); color: var(--status-review); }

/* Form Progression Panel */
.step-wizard {
  background: var(--glass-bg);
  border: 1px solid var(--glass-border);
  border-radius: 24px;
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.step-wizard-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.wizard-nav-btn {
  width: 36px;
  height: 36px;
  border-radius: 12px;
  background: rgba(255,255,255,0.03);
  border: 1px solid var(--border-color);
  color: var(--text-primary);
  display: flex;
  align-items: center;
  justify-content: center;
}

.wizard-nav-btn:hover {
  background: rgba(255, 255, 255, 0.08);
  border-color: var(--brand);
}

.wizard-nav-btn:disabled {
  opacity: 0.2;
  cursor: not-allowed;
}

.step-wizard-info {
  text-align: center;
}

.step-wizard-info span {
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  color: var(--brand);
  letter-spacing: 0.05em;
  display: block;
}

.step-wizard-info strong {
  font-size: 1.15rem;
  font-weight: 800;
  color: var(--text-primary);
  display: block;
  margin-top: 2px;
}

.progress-track {
  height: 6px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 99px;
  overflow: hidden;
  position: relative;
}

.progress-fill {
  height: 100%;
  border-radius: 99px;
  background: linear-gradient(90deg, var(--brand), var(--accent));
  transition: width 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Premium Custom Select Button */
.custom-select-trigger {
  width: 100%;
  height: 52px;
  border-radius: 16px;
  background: var(--input-bg);
  border: 1px solid var(--glass-border);
  padding: 0 1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-weight: 600;
  font-size: 0.95rem;
  color: var(--text-primary);
  transition: var(--transition-smooth);
}

.custom-select-trigger:focus, .custom-select-trigger.active {
  border-color: var(--brand);
  box-shadow: 0 0 10px rgba(16, 185, 129, 0.15);
}

/* Segmented Options Control */
.segmented-control {
  display: flex;
  background: var(--input-bg);
  border: 1px solid var(--glass-border);
  padding: 4px;
  border-radius: 16px;
  position: relative;
  width: 100%;
}

.segmented-option {
  flex: 1;
  text-align: center;
  padding: 0.65rem 0;
  border-radius: 12px;
  font-size: 0.85rem;
  font-weight: 700;
  z-index: 2;
  color: var(--text-secondary);
}

.segmented-option.active {
  color: #0b1c17;
  background: var(--accent);
  box-shadow: 0 4px 10px rgba(163, 230, 53, 0.2);
}

.segmented-option.active.normal {
  background: var(--color-normal);
  color: white;
  box-shadow: 0 4px 10px rgba(59, 130, 246, 0.3);
}

.segmented-option.active.high {
  background: var(--color-high);
  color: white;
  box-shadow: 0 4px 10px rgba(245, 158, 11, 0.3);
}

.segmented-option.active.urgent {
  background: var(--color-urgent);
  color: white;
  box-shadow: 0 4px 10px rgba(239, 68, 68, 0.3);
}

/* Interactive GPS Widget */
.gps-scanner-widget {
  background: var(--input-bg);
  border: 1px solid var(--glass-border);
  border-radius: 20px;
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.gps-action-btn {
  width: 100%;
  height: 48px;
  background: var(--brand);
  color: #0b1c17;
  border-radius: 14px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.gps-action-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(16, 185, 129, 0.4);
}

.gps-map-container {
  height: 120px;
  border-radius: 14px;
  background: rgba(0,0,0,0.4);
  border: 1px solid var(--border-color);
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Radar Scan Animations */
.gps-radar-wave {
  position: absolute;
  width: 100px;
  height: 100px;
  border-radius: 50%;
  border: 2px solid rgba(16, 185, 129, 0.4);
  animation: pulse-wave 2.5s infinite linear;
}
.gps-radar-wave.w2 { animation-delay: 0.8s; }
.gps-radar-wave.w3 { animation-delay: 1.6s; }

@keyframes pulse-wave {
  0% { transform: scale(0.2); opacity: 1; }
  100% { transform: scale(2); opacity: 0; }
}

.gps-radar-sweeper {
  position: absolute;
  width: 100%;
  height: 100%;
  background: conic-gradient(from 0deg at 50% 50%, rgba(16, 185, 129, 0.15) 0deg, transparent 90deg);
  animation: sweep 3s infinite linear;
}

@keyframes sweep {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.gps-ping-point {
  position: relative;
  width: 12px;
  height: 12px;
  background: var(--brand);
  border-radius: 50%;
  box-shadow: 0 0 12px var(--brand);
  z-index: 5;
}

.gps-coordinates-overlay {
  position: absolute;
  bottom: 8px;
  left: 8px;
  right: 8px;
  background: rgba(9, 13, 12, 0.85);
  border-radius: 8px;
  padding: 6px 10px;
  font-family: monospace;
  font-size: 0.72rem;
  color: var(--brand-light);
  display: flex;
  justify-content: space-between;
  border: 1px solid rgba(16, 185, 129, 0.2);
}

/* Photo Upload Module */
.photo-uploader {
  border: 2px dashed rgba(16, 185, 129, 0.2);
  border-radius: 20px;
  background: var(--input-bg);
  padding: 1.5rem;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  transition: var(--transition-smooth);
  cursor: pointer;
}

.photo-uploader:hover {
  border-color: var(--brand);
  background: var(--glass-bg-hover);
}

.photo-uploader svg {
  color: var(--brand);
}

.photo-uploader span {
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--text-secondary);
}

.photo-uploader small {
  font-size: 0.7rem;
  color: var(--text-muted);
}

.photo-preview-box {
  width: 100%;
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid var(--glass-border);
  position: relative;
}

.photo-preview-img {
  width: 100%;
  height: 160px;
  object-fit: cover;
  display: block;
}

.photo-remove-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: rgba(0,0,0,0.6);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Conditional Alert Box */
.conditional-alert {
  display: flex;
  gap: 0.75rem;
  padding: 1rem;
  border-radius: 16px;
  background: var(--glass-bg);
  border: 1px solid var(--glass-border);
  font-size: 0.85rem;
  line-height: 1.45;
  transition: var(--transition-smooth);
}

.conditional-alert svg {
  flex-shrink: 0;
}

.conditional-alert.urgent {
  border-color: rgba(239, 68, 68, 0.3);
  background: rgba(239, 68, 68, 0.05);
  color: #fca5a5;
}

.conditional-alert.urgent svg {
  color: var(--color-urgent);
}

.conditional-alert.safety {
  border-color: rgba(16, 185, 129, 0.3);
  background: rgba(16, 185, 129, 0.05);
}

.conditional-alert.safety svg {
  color: var(--brand);
}

.conditional-alert.equipment {
  border-color: rgba(59, 130, 246, 0.3);
  background: rgba(59, 130, 246, 0.05);
}

.conditional-alert.equipment svg {
  color: var(--color-normal);
}

/* Custom Modal overlay (Bottom Sheet select) */
.modal-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  z-index: 100;
  display: flex;
  align-items: flex-end;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.3s ease;
}

.modal-overlay.open {
  opacity: 1;
  pointer-events: auto;
}

.bottom-sheet {
  width: 100%;
  background: var(--nav-bg);
  border-top: 1px solid var(--glass-border);
  border-top-left-radius: 28px;
  border-top-right-radius: 28px;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  transform: translateY(100%);
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 -10px 40px rgba(0,0,0,0.3);
}

.modal-overlay.open .bottom-sheet {
  transform: translateY(0);
}

.bottom-sheet-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.bottom-sheet-header strong {
  font-size: 1.1rem;
  font-weight: 800;
}

.bottom-sheet-header button {
  color: var(--text-secondary);
}

.select-options-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  max-height: 250px;
  overflow-y: auto;
}

.select-option-row {
  width: 100%;
  padding: 0.85rem 1rem;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-weight: 600;
  font-size: 0.9rem;
  text-align: left;
}

.select-option-row:hover {
  background: rgba(255,255,255,0.03);
}

.select-option-row.selected {
  background: var(--brand-glow);
  color: var(--brand-light);
}

/* Forms general styling */
.form-card {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.input-block {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.input-block span {
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--text-secondary);
  padding-left: 2px;
}

.input-block select {
  display: none; /* We use custom picker buttons */
}

.fake-input {
  width: 100%;
  height: 52px;
  border-radius: 16px;
  background: var(--input-bg);
  border: 1px solid var(--glass-border);
  padding: 0 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  font-size: 0.95rem;
  color: var(--text-primary);
}

.fake-input svg {
  color: var(--brand);
}

.text-area-input {
  width: 100%;
  border-radius: 16px;
  background: var(--input-bg);
  border: 1px solid var(--glass-border);
  padding: 0.85rem 1rem;
  font-weight: 500;
  font-size: 0.95rem;
  color: var(--text-primary);
  min-height: 80px;
  resize: none;
}

.text-area-input:focus {
  border-color: var(--brand);
}

/* Submit Section review page */
.review-checklist {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.review-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.85rem 1rem;
  border-radius: 16px;
  background: var(--glass-bg);
  border: 1px solid var(--glass-border);
  font-weight: 600;
  font-size: 0.9rem;
}

.review-item svg {
  color: var(--brand);
}

.submit-airtable-btn {
  height: 52px;
  border-radius: 16px;
  background: linear-gradient(135deg, var(--brand), var(--brand-light));
  color: #0b1c17;
  font-weight: 800;
  font-size: 1rem;
  margin-top: 1rem;
  box-shadow: 0 4px 15px rgba(16, 185, 129, 0.3);
}

.submit-airtable-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(16, 185, 129, 0.5);
}

/* Spreadsheet Submissions Grid View */
.spreadsheet-box {
  background: var(--glass-bg);
  border: 1px solid var(--glass-border);
  border-radius: 20px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  max-height: 520px;
}

.search-box {
  height: 48px;
  background: var(--input-bg);
  border-bottom: 1px solid var(--border-color);
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0 1rem;
  color: var(--text-secondary);
}

.search-box input {
  width: 100%;
  font-size: 0.9rem;
  font-weight: 500;
}

.search-box input::placeholder {
  color: var(--text-muted);
}

.filter-pills {
  display: flex;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid var(--border-color);
  overflow-x: auto;
}

.filter-pills::-webkit-scrollbar {
  display: none;
}

.filter-pill {
  font-size: 0.75rem;
  font-weight: 700;
  color: var(--text-secondary);
  border: 1px solid var(--border-color);
  padding: 0.35rem 0.75rem;
  border-radius: 99px;
  white-space: nowrap;
}

.filter-pill.active {
  background: var(--brand-glow);
  color: var(--brand-light);
  border-color: var(--brand);
}

.grid-table-container {
  overflow-x: auto;
  overflow-y: auto;
  flex: 1 1 auto;
}

.grid-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.8rem;
  text-align: left;
}

.grid-table th {
  background: rgba(0, 0, 0, 0.2);
  padding: 0.65rem 0.85rem;
  font-weight: 700;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 1px solid var(--border-color);
  white-space: nowrap;
}

.grid-table td {
  padding: 0.75rem 0.85rem;
  border-bottom: 1px solid var(--border-color);
  white-space: nowrap;
  font-weight: 500;
}

.grid-table tr:hover td {
  background: rgba(255, 255, 255, 0.02);
  cursor: pointer;
}

.grid-text-bold {
  font-weight: 700 !important;
  color: var(--text-primary);
}

.grid-meta-tag {
  font-family: monospace;
  color: var(--brand-light);
  font-size: 0.7rem;
}

/* Detail Drawer Slider */
.drawer-backdrop {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  z-index: 110;
  display: flex;
  justify-content: flex-end;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.3s ease;
}

.drawer-backdrop.open {
  opacity: 1;
  pointer-events: auto;
}

.drawer-panel {
  width: 85%;
  height: 100%;
  background: var(--nav-bg);
  border-left: 1px solid var(--glass-border);
  box-shadow: -10px 0 40px rgba(0,0,0,0.3);
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  transform: translateX(100%);
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow-y: auto;
}

.drawer-backdrop.open .drawer-panel {
  transform: translateX(0);
}

.drawer-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid var(--border-color);
  padding-bottom: 1rem;
}

.drawer-header strong {
  font-size: 1.15rem;
  font-weight: 800;
  letter-spacing: -0.02em;
}

.drawer-content {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.drawer-field-row {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.drawer-field-row label {
  font-size: 0.7rem;
  font-weight: 700;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.drawer-field-row div {
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--text-primary);
}

/* Email Automations Visualizer */
.alert-flow {
  background: var(--glass-bg);
  border: 1px solid var(--glass-border);
  border-radius: 24px;
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.alert-flow h3 {
  font-size: 1.15rem;
  font-weight: 800;
  letter-spacing: -0.02em;
  display: flex;
  align-items: center;
  gap: 0.4rem;
}

.flow-container {
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
  position: relative;
}

.flow-card-node {
  background: var(--input-bg);
  border: 1px solid var(--glass-border);
  border-radius: 16px;
  padding: 1rem;
  display: flex;
  align-items: center;
  gap: 0.85rem;
  position: relative;
  transition: var(--transition-smooth);
}

.flow-card-node::before {
  content: '';
  position: absolute;
  top: -10px;
  left: 24px;
  width: 2px;
  height: 10px;
  background: var(--border-color);
  z-index: 1;
}

.flow-card-node:first-child::before {
  display: none;
}

.flow-card-node.active {
  border-color: var(--brand);
  background: var(--brand-glow);
  box-shadow: 0 0 15px rgba(16, 185, 129, 0.15);
  animation: glow-pulse 2s infinite ease-in-out;
}

@keyframes glow-pulse {
  0%, 100% { border-color: rgba(16, 185, 129, 0.4); }
  50% { border-color: var(--brand); }
}

.flow-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid var(--border-color);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-secondary);
}

.flow-card-node.active .flow-icon {
  background: rgba(16, 185, 129, 0.2);
  border-color: var(--brand);
  color: var(--brand-light);
}

.flow-card-node.active.urgent .flow-icon {
  background: rgba(239, 68, 68, 0.2);
  border-color: var(--color-urgent);
  color: #f87171;
}
.flow-card-node.active.urgent {
  border-color: var(--color-urgent);
  background: rgba(239, 68, 68, 0.08);
  box-shadow: 0 0 15px rgba(239, 68, 68, 0.15);
}

.flow-node-text {
  flex: 1;
}

.flow-node-text strong {
  font-size: 0.85rem;
  font-weight: 700;
  display: block;
}

.flow-node-text span {
  font-size: 0.72rem;
  color: var(--text-secondary);
  display: block;
  margin-top: 1px;
}

.flow-trigger-pill {
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  padding: 0.15rem 0.45rem;
  border-radius: 99px;
  border: 1px solid var(--border-color);
  background: rgba(255,255,255,0.02);
}

.flow-card-node.active .flow-trigger-pill {
  background: var(--brand-light);
  color: #0b1c17;
  border-color: var(--brand);
}

.flow-card-node.active.urgent .flow-trigger-pill {
  background: var(--color-urgent);
  color: white;
  border-color: var(--color-urgent);
}

/* Base Mapping Layout */
.base-map {
  background: var(--glass-bg);
  border: 1px solid var(--glass-border);
  border-radius: 24px;
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.base-map h3 {
  font-size: 1.1rem;
  font-weight: 800;
  letter-spacing: -0.02em;
  margin-bottom: 0.25rem;
}

.base-map-tables {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.5rem;
}

.base-map-cell {
  background: var(--input-bg);
  border: 1px solid var(--glass-border);
  border-radius: 14px;
  padding: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  font-size: 0.8rem;
}

.base-map-cell svg {
  color: var(--brand);
}

/* Bottom Nav bar menu */
.bottom-nav {
  position: absolute;
  inset: auto 0 0 0;
  height: 72px;
  background: var(--nav-bg);
  border-top: 1px solid var(--glass-border);
  backdrop-filter: blur(28px);
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  padding: 8px 12px;
  z-index: 90;
}

.bottom-nav-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  border-radius: 16px;
  color: var(--text-secondary);
  font-size: 0.7rem;
  font-weight: 700;
}

.bottom-nav-btn svg {
  transition: var(--transition-spring);
}

.bottom-nav-btn:hover {
  color: var(--brand-light);
}

.bottom-nav-btn.active {
  color: var(--brand-light);
  background: var(--brand-glow);
}

.bottom-nav-btn.active svg {
  transform: scale(1.15);
}

/* Interactive Toast Overlay */
.toast-container {
  position: absolute;
  top: 48px;
  left: 12px;
  right: 12px;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  z-index: 200;
  pointer-events: none;
}

.toast-bubble {
  background: rgba(13, 27, 23, 0.95);
  backdrop-filter: blur(16px);
  border: 1px solid var(--brand);
  border-radius: 16px;
  padding: 0.85rem 1rem;
  box-shadow: 0 10px 25px rgba(0,0,0,0.4);
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: var(--text-primary);
  pointer-events: auto;
  animation: slide-down 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;
}

@keyframes slide-down {
  0% { transform: translateY(-40px); opacity: 0; }
  100% { transform: translateY(0); opacity: 1; }
}

.toast-bubble svg {
  color: var(--brand);
  flex-shrink: 0;
}

.toast-bubble.urgent {
  border-color: var(--color-urgent);
  background: rgba(28, 12, 12, 0.95);
}

.toast-bubble.urgent svg {
  color: var(--color-urgent);
}

.toast-text {
  flex: 1;
}

.toast-text strong {
  font-size: 0.85rem;
  font-weight: 700;
  display: block;
}

.toast-text span {
  font-size: 0.72rem;
  color: var(--text-secondary);
  display: block;
  margin-top: 1px;
}

/* Responsive Overrides */
@media (max-width: 480px) {
  body {
    background: var(--app-bg);
  }
  .desktop-wrapper {
    padding: 0;
    display: block;
  }
  .control-panel {
    display: none; /* Hide desktop panel on real mobile */
  }
  .mobile-app {
    width: 100vw;
    height: 100vh;
  }
  .app-shell {
    width: 100vw;
    height: 100vh;
    border: none;
    border-radius: 0;
    box-shadow: none;
  }
  .app-notch {
    display: none;
  }
  .screen-body {
    padding-top: max(1rem, env(safe-area-inset-top));
    padding-bottom: calc(75px + env(safe-area-inset-bottom));
  }
  .bottom-nav {
    height: calc(72px + env(safe-area-inset-bottom));
    padding-bottom: env(safe-area-inset-bottom);
  }
}
